import { useState, useEffect, useCallback, useRef } from 'react'
import { api, smishingApi } from '../api'
import type { ScanStatus } from '../api'
import { suspicionPill } from './Pill'
import { Pill } from './Pill'
import { ErrorState, errorMessage } from './ErrorState'
import { TriageControls } from './TriageControls'
import { enumLabel, useI18n, type MessageKey, type TFunction } from '../i18n'
import type { SuspiciousDomain, SmishingReport, PaymentIntegration, PaginatedResponse, DiscoverySource } from '../types'

// ── Onglets ──────────────────────────────────────────────────────────────────

type TabKey = 'all' | 'domains' | 'apps' | 'social' | 'sms' | 'payments'

interface TabDef {
  key: TabKey
  labelKey: MessageKey
  icon: string
  source: DiscoverySource | null // null = all
}

const TABS: TabDef[] = [
  { key: 'all',      labelKey: 'discovery.tab.all',      icon: '\u{1F50D}', source: null },
  { key: 'domains',  labelKey: 'discovery.tab.domains',  icon: '\u{1F310}', source: 'CRT_SH' },
  { key: 'apps',     labelKey: 'discovery.tab.apps',     icon: '\u{1F4F1}', source: 'APP_STORE' },
  { key: 'social',   labelKey: 'discovery.tab.social',   icon: '\u{1F465}', source: 'SOCIAL' },
  { key: 'sms',      labelKey: 'discovery.tab.sms',      icon: '\u{1F4AC}', source: 'SMISHING' },
  { key: 'payments', labelKey: 'discovery.tab.payments', icon: '\u{1F4B3}', source: 'PAYMENT_INTEGRATION' },
]

// ── Source metadata ──────────────────────────────────────────────────────────

/** Icône et couleur par source. Le libellé vient du catalogue. */
const SOURCE_META: Record<string, { icon: string; color: string }> = {
  CRT_SH:        { icon: '\u{1F512}', color: 'var(--info)' },
  APP_STORE:     { icon: '\u{1F4F1}', color: 'var(--info)' },
  SOCIAL:        { icon: '\u{1F465}', color: '#0ea5e9' },
  SMISHING:      { icon: '\u{1F4AC}', color: 'var(--crit)' },
  DNS_MONITOR:   { icon: '\u{1F50E}', color: 'var(--warn)' },
  SEARCH_ENGINE: { icon: '\u{1F50E}', color: 'var(--ok-fg)' },
}

// ── Données fallback ─────────────────────────────────────────────────────────



// ── Composant principal ──────────────────────────────────────────────────────

type ScanType = 'domains' | 'apps' | 'social' | 'payments'

export function DiscoveryPanel() {
  const { t, fmt } = useI18n()
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [domainData, setDomainData] = useState<PaginatedResponse<SuspiciousDomain> | null>(null)
  const [smsData, setSmsData] = useState<PaginatedResponse<SmishingReport> | null>(null)
  const [paymentData, setPaymentData] = useState<PaginatedResponse<PaymentIntegration> | null>(null)
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tabCounts, setTabCounts] = useState<Record<TabKey, number>>({ all: 0, domains: 0, apps: 0, social: 0, sms: 0, payments: 0 })
  // `found` et `ok` plutôt qu'une relecture du message : chercher un mot dans
  // une phrase traduite casserait dès qu'on change de langue.
  const [correlateResult, setCorrelateResult] = useState<{ id: string; message: string; found: boolean } | null>(null)
  const [scanning, setScanning] = useState<Record<ScanType, boolean>>({ domains: false, apps: false, social: false, payments: false })
  const [scanResult, setScanResult] = useState<{ type: ScanType; message: string; ok: boolean } | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch tab counts on mount
  const fetchCounts = useCallback(async () => {
    try {
      const [all, domains, apps, social, sms, payments] = await Promise.allSettled([
        api.getSuspiciousDomains({ limit: 1 }),
        api.getSuspiciousDomains({ source: 'CRT_SH', limit: 1 }),
        api.getSuspiciousDomains({ source: 'APP_STORE', limit: 1 }),
        api.getSuspiciousDomains({ source: 'SOCIAL', limit: 1 }),
        smishingApi.getReports({ limit: 1 }),
        api.getPaymentIntegrations({ limit: 1 }),
      ])
      // Un compteur dont la requete a echoue reste nul : on n'invente pas de total.
      const total = (r: PromiseSettledResult<{ total: number }>) =>
        r.status === 'fulfilled' ? r.value.total : 0
      setTabCounts({
        all: total(all),
        domains: total(domains),
        apps: total(apps),
        social: total(social),
        sms: total(sms),
        payments: total(payments),
      })
    } catch {
      setTabCounts({ all: 0, domains: 0, apps: 0, social: 0, sms: 0, payments: 0 })
    }
  }, [])

  useEffect(() => { fetchCounts() }, [fetchCounts])

  const syncScanStatus = useCallback(async () => {
    try {
      const status = await api.getScanStatus()
      setScanning({
        domains: status.domains,
        apps: status.apps,
        social: status.social,
        payments: status.payments,
      })
      return status
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    syncScanStatus()
  }, [syncScanStatus])

  useEffect(() => {
    const anyRunning = Object.values(scanning).some(Boolean)
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const status = await syncScanStatus()
        if (status && !status.domains && !status.apps && !status.social && !status.payments) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
          doFetch()
          fetchCounts()
        }
      }, 3000)
    }
    if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [scanning, syncScanStatus])

  const triggerScan = useCallback(async (type: ScanType) => {
    if (scanning[type]) return
    setScanning(prev => ({ ...prev, [type]: true }))
    setScanResult(null)
    try {
      let message: string
      if (type === 'domains') {
        const res = await api.triggerScanAll()
        message = t('discovery.scanDomainsResult', { total: res.total, saved: res.saved })
      } else if (type === 'apps') {
        const res = await api.triggerScanApps()
        message = t('discovery.scanAppsResult', { total: res.total, saved: res.saved })
      } else if (type === 'social') {
        const res = await api.triggerScanSocial()
        message = t('discovery.scanSocialResult', { total: res.total, saved: res.saved })
      } else {
        const res = await api.triggerScanPayments()
        message = t('discovery.scanPaymentsResult', { total: res.total, undeclared: res.undeclared })
      }
      setScanResult({ type, message, ok: true })
      doFetch()
      fetchCounts()
    } catch (err) {
      setScanResult({ type, message: t('discovery.scanError', { error: errorMessage(err) }), ok: false })
    } finally {
      await syncScanStatus()
    }
  }, [scanning, syncScanStatus, t])

  useEffect(() => {
    if (!scanResult) return
    const t = setTimeout(() => setScanResult(null), 15_000)
    return () => clearTimeout(t)
  }, [scanResult])

  const anyScanRunning = Object.values(scanning).some(Boolean)

  // Fetch data for current tab
  const doFetch = useCallback(async () => {
    setLoading(true)
    try {
      if (activeTab === 'sms') {
        const res = await smishingApi.getReports({ page, limit: 20 })
        setSmsData(res)
      } else if (activeTab === 'payments') {
        const res = await api.getPaymentIntegrations({
          complianceStatus: filter || undefined,
          page,
          limit: 20,
        })
        setPaymentData(res)
      } else {
        const tab = TABS.find(t => t.key === activeTab)!
        const res = await api.getSuspiciousDomains({
          suspicionLevel: filter || undefined,
          source: tab.source ?? undefined,
          page,
          limit: 20,
        })
        setDomainData(res)
      }
      setError(null)
    } catch (err: unknown) {
      // Outil de conformite : on n'affiche jamais de detections inventees.
      setSmsData(null)
      setPaymentData(null)
      setDomainData(null)
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [activeTab, filter, page])

  useEffect(() => { doFetch() }, [doFetch])
  useEffect(() => { setPage(1) }, [activeTab, filter])

  // Auto-clear correlate result
  useEffect(() => {
    if (!correlateResult) return
    const t = setTimeout(() => setCorrelateResult(null), 10_000)
    return () => clearTimeout(t)
  }, [correlateResult])

  const handleCorrelate = async (report: SmishingReport) => {
    if (report.extractedUrls.length === 0) {
      setCorrelateResult({ id: report.id, message: t('discovery.sms.noUrl'), found: false })
      return
    }
    try {
      // Search for matching domains
      const url = report.extractedUrls[0]
      let hostname: string
      try { hostname = new URL(url).hostname } catch { hostname = url }
      const res = await api.getSuspiciousDomains({ limit: 5 })
      const match = res.data.find(d => hostname.includes(d.domain) || d.domain.includes(hostname))
      if (match) {
        setCorrelateResult({
          id: report.id,
          found: true,
          message: t('discovery.sms.correlationFound', {
            host: hostname,
            domain: match.domain,
            brand: match.matchedBrand,
            level: enumLabel(t, 'suspicion', match.suspicionLevel, match.suspicionLevel),
          }),
        })
      } else {
        setCorrelateResult({
          id: report.id,
          found: false,
          message: t('discovery.sms.correlationNone', { host: hostname }),
        })
      }
    } catch {
      setCorrelateResult({ id: report.id, message: t('discovery.sms.correlationError'), found: false })
    }
  }

  const currentPaginated = activeTab === 'sms' ? smsData : activeTab === 'payments' ? paymentData : domainData

  return (
    <div>
      {/* ── Scan toolbar ─────────────────────────── */}
      <div className="scan-toolbar">
        <span className="scan-toolbar-label">{t('discovery.runScan')}</span>
        {([
          { type: 'domains' as ScanType, icon: '\u{1F512}', label: t('discovery.scan.domains') },
          { type: 'apps' as ScanType, icon: '\u{1F4F1}', label: t('discovery.scan.apps') },
          { type: 'social' as ScanType, icon: '\u{1F465}', label: t('discovery.scan.social') },
          { type: 'payments' as ScanType, icon: '\u{1F4B3}', label: t('discovery.scan.payments') },
        ]).map(s => (
          <button
            key={s.type}
            className={`scan-btn ${scanning[s.type] ? 'scan-btn-active' : ''}`}
            disabled={scanning[s.type]}
            onClick={() => triggerScan(s.type)}
            title={scanning[s.type] ? t('discovery.scanRunning') : t('discovery.scanStart', { label: s.label })}
          >
            {scanning[s.type] ? (
              <span className="scan-spinner" />
            ) : (
              <span>{s.icon}</span>
            )}
            {scanning[s.type] ? t('discovery.scanRunning') : s.label}
          </button>
        ))}
      </div>

      {scanResult && (
        <div className={`scan-result ${scanResult.ok ? 'scan-result-ok' : 'scan-result-error'}`}>
          {scanResult.message}
        </div>
      )}

      {/* ── Sub-tabs ──────────────────────────────── */}
      <div className="discovery-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`discovery-tab ${activeTab === tab.key ? 'discovery-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="discovery-tab-icon">{tab.icon}</span>
            {t(tab.labelKey)}
            <span className="discovery-tab-count">{tabCounts[tab.key]}</span>
          </button>
        ))}
      </div>

      {/* ── Filters ──────────────────────────────────── */}
      {activeTab !== 'sms' && activeTab !== 'payments' && (
        <div className="discovery-filters">
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">{t('discovery.allLevels')}</option>
            <option value="CRITICAL">{t('suspicion.CRITICAL')}</option>
            <option value="HIGH">{t('suspicion.HIGH')}</option>
            <option value="MEDIUM">{t('suspicion.MEDIUM')}</option>
            <option value="LOW">{t('suspicion.LOW')}</option>
          </select>
          <button className="btn-refresh" onClick={() => { doFetch(); fetchCounts() }}>
            ↻ {t('common.refresh')}
          </button>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="discovery-filters">
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">{t('discovery.allStatuses')}</option>
            <option value="UNDECLARED">{t('discovery.pay.compliance.UNDECLARED')}</option>
            <option value="COMPLIANT">{t('discovery.pay.compliance.COMPLIANT')}</option>
            <option value="NON_COMPLIANT">{t('discovery.pay.compliance.NON_COMPLIANT')}</option>
            <option value="PENDING_REVIEW">{t('discovery.pay.compliance.PENDING_REVIEW')}</option>
          </select>
          <button className="btn-refresh" onClick={() => { doFetch(); fetchCounts() }}>
            ↻ {t('common.refresh')}
          </button>
        </div>
      )}

      {activeTab === 'sms' && (
        <div className="discovery-filters">
          <button className="btn-refresh" onClick={() => { doFetch(); fetchCounts() }}>
            ↻ {t('common.refresh')}
          </button>
        </div>
      )}

      {error && (
        <ErrorState
          message={error}
          onRetry={() => { void doFetch(); void fetchCounts() }}
        />
      )}

      {/* ── Content ───────────────────────────────── */}
      <div className="discovery-grid" hidden={!!error}>
        {loading && !currentPaginated && <div className="table-empty">{t('common.loading')}</div>}

        {/* Domain / App / Social cards */}
        {activeTab !== 'sms' && activeTab !== 'payments' && domainData?.data.map(d => (
          <DomainCard key={d.id} domain={d} onChanged={doFetch} />
        ))}

        {/* Payment integration cards */}
        {activeTab === 'payments' && paymentData?.data.map(p => (
          <PaymentCard key={p.id} payment={p} />
        ))}

        {/* SMS cards */}
        {activeTab === 'sms' && smsData?.data.map(r => (
          <SmsCard
            key={r.id}
            report={r}
            onCorrelate={handleCorrelate}
            correlateResult={correlateResult?.id === r.id ? correlateResult : null}
          />
        ))}

        {currentPaginated && currentPaginated.data.length === 0 && (
          <div className="table-empty">
            {activeTab === 'sms' ? t('discovery.emptySms') : t('discovery.empty')}
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────── */}
      {currentPaginated && currentPaginated.totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← {t('common.previous')}
          </button>
          <span className="page-info">
            {t('common.page', { page: currentPaginated.page, pages: currentPaginated.totalPages })}
          </span>
          <button disabled={page >= currentPaginated.totalPages} onClick={() => setPage(page + 1)}>
            {t('common.next')} →
          </button>
        </div>
      )}
    </div>
  )
}

// ── Domain/App/Social card ──────────────────────────────────────────────────

function DomainCard({ domain: d, onChanged }: { domain: SuspiciousDomain; onChanged: () => void }) {
  const { t, fmt } = useI18n()
  const meta = SOURCE_META[d.source] ?? { icon: '❓', color: 'var(--text-muted)' }
  const sourceLabel = enumLabel(t, 'discovery.source', d.source, d.source)

  // Parse app info from notes if source is APP_STORE
  const appInfo = d.source === 'APP_STORE' ? parseAppNotes(d.notes) : null
  const socialInfo = d.source === 'SOCIAL' ? parseSocialNotes(d.notes) : null

  return (
    <div className={`discovery-card ${d.suspicionLevel === 'CRITICAL' ? 'discovery-critical' : ''}`}>
      <div className="discovery-top">
        <div>
          <div className="discovery-domain">
            <span className="discovery-source-badge" style={{ background: meta.color }}>
              {meta.icon} {sourceLabel}
            </span>
            {d.source === 'APP_STORE' && appInfo
              ? appInfo.appName
              : d.source === 'SOCIAL' && socialInfo
              ? socialInfo.displayName
              : d.domain}
          </div>
          <div className="discovery-meta">
            {t('discovery.brandLabel')} <strong>{d.matchedBrand}</strong>
            {d.source === 'CRT_SH' && (
              <> &middot; {d.matchType} &middot; {d.isActive ? t('discovery.active') : d.isActive === false ? t('discovery.inactive') : t('common.unknown')}</>
            )}
            {d.source === 'APP_STORE' && appInfo && <> &middot; {t('discovery.devPrefix')} {appInfo.developer} &middot; {appInfo.store}</>}
            {d.source === 'SOCIAL' && socialInfo && <> &middot; {socialInfo.platform}</>}
          </div>
          {appInfo?.storeUrl && (
            <div className="discovery-meta">
              <a href={appInfo.storeUrl} target="_blank" rel="noopener" className="discovery-link">
                {t('discovery.viewSource')}
              </a>
            </div>
          )}
          {d.source === 'SOCIAL' && socialInfo && (
            <div className="discovery-meta">
              {socialInfo.url && (
                <a href={socialInfo.url} target="_blank" rel="noopener" className="discovery-link">
                  {t('discovery.viewPost', { platform: socialInfo.platform })}
                </a>
              )}
              {socialInfo.via && (
                <span className="discovery-via">
                  {t('discovery.via', {
                    source: socialInfo.via === 'SERP_GOOGLE' ? 'SerpAPI' : socialInfo.via === 'TELEGRAM_BOT' ? 'Telegram' : socialInfo.via,
                  })}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="discovery-right">
          {suspicionPill(d.suspicionLevel)}
          {d.similarityScore > 0 && (
            <span className="discovery-score">{t('discovery.similarity', { score: d.similarityScore })}</span>
          )}
        </div>
      </div>
      {socialInfo?.snippet && (
        <div className="discovery-snippet">{socialInfo.snippet}</div>
      )}
      <div className="discovery-bottom">
        <span className="td-muted">
          {d.detectedAgo ?? fmt.dateTime(d.detectedAt)}
        </span>
        {d.screenshotUrl && (
          <a href={d.screenshotUrl} target="_blank" rel="noopener" className="btn-small">
            {t('discovery.screenshot')}
          </a>
        )}
      </div>

      <TriageControls
        kind="discovery"
        id={d.id}
        status={d.triageStatus}
        assignedTo={d.assignedTo}
        onChanged={onChanged}
      />
    </div>
  )
}

// ── SMS card ────────────────────────────────────────────────────────────────

function SmsCard({ report: r, onCorrelate, correlateResult }: {
  report: SmishingReport
  onCorrelate: (r: SmishingReport) => void
  correlateResult: { message: string; found: boolean } | null
}) {
  const { t, fmt } = useI18n()

  /** Couleur du statut ; le libellé vient du catalogue. */
  const statusColors: Record<string, string> = {
    PENDING: 'var(--warn)',
    CONFIRMED_SMISHING: 'var(--crit)',
    FALSE_POSITIVE: 'var(--ok-fg)',
    ESCALATED: 'var(--info)',
  }
  const statusColor = statusColors[r.status] ?? 'var(--text-muted)'
  const statusLabel = enumLabel(t, 'discovery.sms.status', r.status, r.status)

  return (
    <div className={`discovery-card ${r.suspicionLevel === 'CRITICAL' ? 'discovery-critical' : ''}`}>
      <div className="discovery-top">
        <div>
          <div className="discovery-domain">
            <span className="discovery-source-badge" style={{ background: 'var(--crit)' }}>
              {'\u{1F4AC}'} SMS
            </span>
            {t('discovery.sms.sender')} <strong>{r.senderId}</strong>
            {r.senderIdSpoofed && (
              <span className="sms-spoofed-badge">{t('discovery.sms.spoofed', { brand: r.spoofedBrand ?? '—' })}</span>
            )}
          </div>
          <div className="discovery-meta">
            {t('discovery.sms.classification')}{' '}
            <strong>{enumLabel(t, 'discovery.sms.class', r.classification, r.classification)}</strong>
            &middot; {t('discovery.sms.score', { score: r.riskScore })}
            {r.country && <> &middot; {t('discovery.sms.countryLabel', { country: enumLabel(t, 'country', r.country, r.country) })}</>}
          </div>
        </div>
        <div className="discovery-right">
          {suspicionPill(r.suspicionLevel)}
          <span className="sms-status" style={{ color: statusColor }}>{statusLabel}</span>
        </div>
      </div>

      <div className="sms-body">{r.smsBody}</div>

      {r.extractedUrls.length > 0 && (
        <div className="sms-urls">
          {t('discovery.sms.extractedUrls')} {r.extractedUrls.map((u, i) => (
            <span key={i} className="sms-url-tag">{u}</span>
          ))}
        </div>
      )}

      {correlateResult && (
        <div className={`scan-result ${correlateResult.found ? 'scan-result-ok' : 'scan-result-info'}`}>
          {correlateResult.message}
        </div>
      )}

      <div className="discovery-bottom">
        <span className="td-muted">
          {t('discovery.sms.reportedOn', { date: fmt.dateTime(r.reportedAt) })}
        </span>
        <div className="sms-actions">
          {r.extractedUrls.length > 0 && (
            <button className="btn-correlate" onClick={() => onCorrelate(r)}>
              {'\u{1F517}'} {t('discovery.sms.correlate')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Helpers to parse notes ──────────────────────────────────────────────────

function parseAppNotes(notes: string): { appName: string; developer: string; store: string; storeUrl: string } | null {
  const appMatch = notes.match(/App: ([^|]+)/)
  const devMatch = notes.match(/Dev: ([^|]+)/)
  const storeMatch = notes.match(/Store: ([^|]+)/)
  const urlMatch = notes.match(/URL: (.+)/)
  if (!appMatch) return null
  return {
    appName: appMatch[1].trim(),
    // Valeurs non traduites : elles ne sont pas affichées seules, et une
    // absence de développeur se lit mieux comme un tiret que comme un mot.
    developer: devMatch?.[1].trim() ?? '—',
    store: storeMatch?.[1].trim() ?? '—',
    storeUrl: urlMatch?.[1].trim() ?? '#',
  }
}

// ── Payment integration card ──────────────────────────────────────────────

function PaymentCard({ payment: p }: { payment: PaymentIntegration }) {
  const { t, fmt } = useI18n()

  const complianceVariants: Record<string, 'ok' | 'warning' | 'critical' | 'info' | 'neutral'> = {
    COMPLIANT: 'ok',
    NON_COMPLIANT: 'critical',
    UNDECLARED: 'warning',
    PENDING_REVIEW: 'info',
  }
  const complianceVariant = complianceVariants[p.complianceStatus] ?? 'neutral'
  const complianceLabel = enumLabel(t, 'discovery.pay.compliance', p.complianceStatus, p.complianceStatus)

  return (
    <div className={`discovery-card ${p.complianceStatus === 'NON_COMPLIANT' ? 'discovery-critical' : ''}`}>
      <div className="discovery-top">
        <div>
          <div className="discovery-domain">
            <span className="discovery-source-badge" style={{ background: 'var(--ok-fg)' }}>
              {'\u{1F4B3}'} {t('discovery.pay.badge')}
            </span>
            {p.pageTitle || p.domain}
          </div>
          <div className="discovery-meta">
            {t('discovery.pay.module')} <strong>{p.matchedBrand}</strong>
            &middot; {enumLabel(t, 'discovery.pay.match', p.merchantMatchStatus, p.merchantMatchStatus)}
            {p.country && <> &middot; {t('discovery.sms.countryLabel', { country: enumLabel(t, 'country', p.country, p.country) })}</>}
          </div>
          <div className="discovery-meta">
            <a href={p.url} target="_blank" rel="noopener" className="discovery-link">
              {p.domain} ↗
            </a>
          </div>
        </div>
        <div className="discovery-right">
          <Pill label={complianceLabel} variant={complianceVariant} title={p.complianceStatus} />
          {p.riskScore > 0 && <span className="discovery-score">{t('discovery.pay.risk', { score: p.riskScore })}</span>}
        </div>
      </div>

      {p.matchedSignatures.length > 0 && (
        <div className="brand-section">
          <div className="brand-section-title">{t('discovery.pay.signatures')}</div>
          <div className="brand-tags">
            {p.matchedSignatures.map((s, i) => <span key={i} className="brand-tag">{s}</span>)}
          </div>
        </div>
      )}

      {p.snippet && (
        <div className="discovery-snippet">{p.snippet}</div>
      )}

      <div className="discovery-bottom">
        <span className="td-muted">
          {p.detectedAgo ?? fmt.dateTime(p.detectedAt)}
        </span>
        <div className="sms-actions">
          {p.action && (
            <span className="brand-tag brand-tag-typo">
              {t('discovery.pay.actionLabel', { action: enumLabel(t, 'discovery.pay.action', p.action, p.action) })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function parseSocialNotes(notes: string): { platform: string; displayName: string; snippet: string; url?: string; via?: string } | null {
  // Format: "tiktok: DisplayName | snippet text | URL: https://... | Via: SERP_GOOGLE"
  const urlMatch = notes.match(/\| URL: ([^\s|]+)/)
  const viaMatch = notes.match(/\| Via: (\w+)/)
  const cleanNotes = notes.replace(/\s*\| URL: [^\s|]+/, '').replace(/\s*\| Via: \w+/, '')
  const match = cleanNotes.match(/^(\w+):\s*([^|]+)\|\s*(.+)$/)
  if (!match) return { platform: 'Social', displayName: notes.slice(0, 50), snippet: notes, url: urlMatch?.[1], via: viaMatch?.[1] }
  return {
    platform: match[1].trim(),
    displayName: match[2].trim(),
    snippet: match[3].trim(),
    url: urlMatch?.[1],
    via: viaMatch?.[1],
  }
}
